import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

interface ActivationConfirmDialogProps {
  open: boolean;
  workflowName: string;
  triggerDescription: string;
  actionLabels: string[];
  isSubmitting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export default function ActivationConfirmDialog({
  open,
  workflowName,
  triggerDescription,
  actionLabels,
  isSubmitting,
  onCancel,
  onConfirm,
}: ActivationConfirmDialogProps) {
  const [step, setStep] = useState<1 | 2>(1);
  const [confirmText, setConfirmText] = useState("");

  useEffect(() => {
    if (open) {
      setStep(1);
      setConfirmText("");
    }
  }, [open]);

  const canConfirm = confirmText.trim().toUpperCase() === "ACTIVATE";

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <WarningAmberIcon sx={{ color: "warning.main" }} />
        Turn on automatic execution?
      </DialogTitle>
      <DialogContent>
        {step === 1 ? (
          <>
            <Typography sx={{ mb: 2 }}>
              &lsquo;{workflowName}&rsquo; will run automatically, with no further approval, whenever{" "}
              {triggerDescription}. It will perform:
            </Typography>
            <List dense sx={{ bgcolor: "action.hover", borderRadius: 1 }}>
              {actionLabels.length === 0 ? (
                <ListItem>
                  <ListItemText primary="No action steps configured yet." />
                </ListItem>
              ) : (
                actionLabels.map((label, idx) => (
                  <ListItem key={`${label}-${idx}`}>
                    <ListItemText primary={`${idx + 1}. ${label}`} />
                  </ListItem>
                ))
              )}
            </List>
          </>
        ) : (
          <Box>
            <Typography sx={{ mb: 2 }}>
              Type <strong>ACTIVATE</strong> to confirm you want this to run without manual approval.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              size="small"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="ACTIVATE"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        {step === 1 ? (
          <>
            <Button onClick={onCancel}>Cancel</Button>
            <Button variant="contained" color="warning" onClick={() => setStep(2)}>
              Continue
            </Button>
          </>
        ) : (
          <>
            <Button onClick={() => setStep(1)} disabled={isSubmitting}>
              Back
            </Button>
            <Button
              variant="contained"
              color="warning"
              disabled={!canConfirm || isSubmitting}
              onClick={onConfirm}
            >
              {isSubmitting ? "Activating…" : "Activate Workflow"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
